import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

// Import services
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser
} from '../../services/userService'; // Changed to userService

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';

const User = () => { // Changed component name
  // Meta title
  document.title = "User"; // Changed document title

  // State management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [user, setUser] = useState(null); // Changed state variable name
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Tambahkan state baru untuk loading submit
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    level: "" // Changed filter name
  });
  const [deleteModal, setDeleteModal] = useState(false);

  // Function to handle closing delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setUser(null);
  };

  // Function to confirm user deletion
  const handleConfirmDelete = async () => {
    try {
      if (user && user.email) {
        const response = await deleteUser(user.email);
        if (response.success) {
          toast.success("User berhasil dihapus");
          fetchData(pagination.page, pagination.per_page, filters);
        } else {
          toast.error(response.message || "Gagal menghapus user");
        }
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.message || "Gagal menghapus user");
    } finally {
      handleCloseDeleteModal();
    }
  };

  // Fetch data function
  const fetchData = async (page = 1, per_page = 10, filterParams = {}) => {
    try {
      setLoading(true);
      const response = await fetchUsers(page, per_page, filterParams); // Changed to fetchUsers

      setData(response.data || []);
      setPagination({
        page: response.meta?.page || 1,
        per_page: response.meta?.per_page || 10,
        total: response.meta?.total || 0,
        last_page: response.meta?.last_page || Math.ceil((response.meta?.total || 0) / (response.meta?.per_page || 10))
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data user");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));

    // Apply filters immediately when changed
    fetchData(1, pagination.per_page, {
      ...filters,
      [name]: value
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      level: ""
    });
    fetchData();
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: user?.full_name || "",
      email: user?.email || "",
      nomor_hp: user?.nomor_hp || "",
      level: user?.level || "",
      password: "",
      password_confirmation: "",
    },

    validationSchema: Yup.object().shape({
      full_name: Yup.string().required("Nama Lengkap wajib diisi"),
      email: Yup.string().email("Format email tidak valid").required("Email wajib diisi"),
      nomor_hp: Yup.string().required("Nomor HP wajib diisi"),
      level: Yup.string().required("Level wajib diisi"),
      
      // Menggunakan .when() untuk validasi kondisional
      password: Yup.string().when('email', {
        is: (email) => !isEdit,
        then: (schema) => schema.required("Password wajib diisi")
      }),
      password_confirmation: Yup.string().when('email', {
        is: (email) => !isEdit,
        then: (schema) => schema
          .required("Konfirmasi Password wajib diisi")
          .oneOf([Yup.ref('password'), null], "Password tidak cocok")
      }),
    }),

    onSubmit: async (values) => {
      setIsSubmitting(true); // Atur state loading menjadi true
      try {
        let response;
        const formData = new FormData();
        formData.append("full_name", values.full_name);
        formData.append("email", values.email);
        formData.append("nomor_hp", values.nomor_hp);
        formData.append("level", values.level);

        if (!isEdit) {
          formData.append("password", values.password);
          formData.append("password_confirmation", values.password_confirmation);
        }

        if (isEdit) {
          response = await updateUser(user.user_id, formData);
        } else {
          response = await createUser(formData);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "User berhasil diperbarui" : "User berhasil ditambahkan"));
          fetchData(pagination.page, pagination.per_page, filters);
          toggleModal();
        } else {
          if (response.code === 409) {
            toast.error(response.message || "Terjadi Kesalahan saat menyimpan");
          } else {
            toast.error(response.message || "Gagal menyimpan user");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan");
      } finally {
        setIsSubmitting(false); // Atur state loading kembali ke false
      }
    },
  });

  // Modal handling
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setUser(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  const handleUserClick = (userData) => { // Changed function name
    setUser(userData);
    setIsEdit(true);
    toggleModal();
  };

  // Delete handling
  const onClickDelete = (userData) => { // Changed function name
    setUser(userData);
    setDeleteModal(true);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Nama Lengkap", // Changed column header
        accessorKey: "full_name", // Changed accessorKey
        enableColumnFilter: false,
      },
      {
        header: 'Email', // Changed column header
        accessorKey: "email", // Changed accessorKey
        enableColumnFilter: false,
      },
      {
        header: 'Nomor HP', // Changed column header
        accessorKey: "nomor_hp", // Changed accessorKey
        enableColumnFilter: false,
      },
      {
        header: 'Level', // Changed column header
        accessorKey: "level", // Changed accessorKey
        enableColumnFilter: false,
      },
      {
        header: 'Created At',
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          return date.toISOString().split('T')[0];
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
             <Link
                to="#"
                className="btn btn-sm btn-soft-info"
                onClick={() => {
                  const userData = cellProps.row.original;
                  handleUserClick(userData);
                }}
                id={`edittooltip-${cellProps.row.original.user_id}`}
            >
            <i className="mdi mdi-pencil-outline" />
            <UncontrolledTooltip
              placement="top"
              target={`edittooltip-${cellProps.row.original.user_id}`}
              delay={{ show: 250, hide: 0 }} // Perbaikan
            >
                Edit
            </UncontrolledTooltip>
            </Link>
            <Link
                to="#"
                className="btn btn-sm btn-soft-danger"
                onClick={() => {
                    const userData = cellProps.row.original;
                    onClickDelete(userData);
                }}
                id={`deletetooltip-${cellProps.row.original.user_id}`}
                >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${cellProps.row.original.user_id}`}
                  delay={{ show: 250, hide: 0 }} // Perbaikan
                >
                    Delete
                </UncontrolledTooltip>
                </Link>
          </div>
        )
      },
    ],
    []
  );

  // Custom Pagination Component (no changes needed here as it's generic)
  const CustomPagination = () => {
    const { page, last_page } = pagination;
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, pagination.per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
      return pages;
    };
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * pagination.per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * pagination.per_page, pagination.total)}</span> dari{' '}
          <span className="fw-semibold">{pagination.total}</span> data
        </div>

        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, pagination.per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers()}

            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, pagination.per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };


  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="User" breadcrumbItem="List User" /> {/* Changed breadcrumb */}
          {
            loading ? <Spinners setLoading={setLoading} />
              :
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody className="border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0 card-title flex-grow-1">List User</h5> {/* Changed card title */}
                        <div className="d-flex align-items-center">
                          <Button color="primary" onClick={toggleModal}>
                            <i className="mdi mdi-plus me-1"></i>
                            Tambah
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                    <CardBody>
                      {/* Filter Section */}
                      <div className="mb-4">
                        <Row className="align-items-end">
                          <Col md={3} className="mb-3">
                            <Label htmlFor="levelFilter">Level</Label> {/* Changed label */}
                            <Input
                              id="levelFilter"
                              type="select"
                              name="level"
                              value={filters.level}
                              onChange={handleFilterChange}
                              className="form-select"
                            >
                              <option value="">Semua Level</option>
                              <option value="super-admin">Super Admin</option>
                              <option value="student">Student</option>
                            </Input>
                          </Col>
                          <Col md={2} className="mb-3">
                            <Label style={{ visibility: 'hidden' }}>Reset Filter</Label>
                            <Button color="danger" outline onClick={clearFilters} className="w-100">
                              <i className="mdi mdi-refresh me-1"></i> Reset
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      <CustomPagination />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit User" : "Tambah User"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label>Nama Lengkap</Label>
                      <Input
                        name="full_name"
                        type="text"
                        placeholder="Nama Lengkap"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.full_name || ""}
                        invalid={!!(validation.touched.full_name && validation.errors.full_name)}
                      />
                      <FormFeedback>{validation.errors.full_name}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Email</Label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={!!(validation.touched.email && validation.errors.email)}
                      />
                      <FormFeedback>{validation.errors.email}</FormFeedback>
                    </div>
                    <div className="mb-3">
                      <Label>Nomor HP</Label>
                      <Input
                        name="nomor_hp"
                        type="tel"
                        placeholder="Nomor HP"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nomor_hp || ""}
                        invalid={!!(validation.touched.nomor_hp && validation.errors.nomor_hp)}
                      />
                      <FormFeedback>{validation.errors.nomor_hp}</FormFeedback>
                    </div>
                    {/* Input Password hanya muncul saat tambah user */}
                    {!isEdit && (
                      <div className="mb-3">
                        <Label>Password</Label>
                        <Input
                          name="password"
                          type="password"
                          placeholder="Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password || ""}
                          invalid={!!(validation.touched.password && validation.errors.password)}
                        />
                        <FormFeedback>{validation.errors.password}</FormFeedback>
                      </div>
                    )}
                    {/* Input Konfirmasi Password hanya muncul saat tambah user */}
                    {!isEdit && (
                      <div className="mb-3">
                        <Label>Konfirmasi Password</Label>
                        <Input
                          name="password_confirmation"
                          type="password"
                          placeholder="Konfirmasi Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password_confirmation || ""}
                          invalid={!!(validation.touched.password_confirmation && validation.errors.password_confirmation)}
                        />
                        <FormFeedback>{validation.errors.password_confirmation}</FormFeedback>
                      </div>
                    )}
                    <div className="mb-3">
                      <Label>Level</Label>
                      <Input
                        name="level"
                        type="select"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.level || ""}
                        invalid={!!(validation.touched.level && validation.errors.level)}
                      >
                        <option value="">Pilih Level</option>
                        <option value="super-admin">Super Admin</option>
                        <option value="student">Student</option>
                      </Input>
                      <FormFeedback>{validation.errors.level}</FormFeedback>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <div className="text-end">
                    <Button color="success" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Menyimpan..." : (isEdit ? "Update" : "Simpan")}
                    </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

export default User;